import { useState } from "react";

import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import Divider from "@mui/material/Divider";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import LayersIcon from "@mui/icons-material/Layers";
import SearchIcon from "@mui/icons-material/Search";
import PrintIcon from "@mui/icons-material/Print";
import EditIcon from "@mui/icons-material/Edit";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import InfoIcon from "@mui/icons-material/Info";
import GridOnIcon from "@mui/icons-material/GridOn";
import Tooltip from "@mui/material/Tooltip";
import { actionMenuToggled, actionMenuSearchTypeToggled } from "./AppBarRedux";
import { useDispatch, useSelector } from "react-redux";
import MoreMenu from "./MenuMore";
import UpdateStatus from "./MenuStatus";
import { actionChangeFilter, actionSearchVisibleToggled } from "../Search";
import { actionAppToggleComponent, selectorAppIsSmallScreen } from "../App";
import { actionLayerCompactToggled } from "../Layer";
import { actionPrintExpandToggled } from "../Print";
import { actionSheetExpandToggled } from "../Spreadsheet";
import { actionDrawExpandToggled } from "../Draw";
import { actionCustomLayerListToggled } from "../CustomLayer";

import ListItemButton from "@mui/material/ListItemButton";
import { trackEvent } from "../../tracking";
import { MGIS_DOC_LINK } from "../Shared/util";

const root_ = (theme) => ({
  width: "20vw",
  height: "100%",
  minWidth: 260,
  padding: "0px",
  backgroundColor: theme.palette.background.paper,
  [theme.breakpoints.down("sm")]: {
    width: "70vw",
  },
  display: "flex",
  flexDirection: "column",

  // Needed as of mui v7 to allow help_ topMargin: auto to take up
  // all the available vertical space.
  "& .MuiListItemButton-root": { flexGrow: 0 },
});

const subsection_ = (theme) => ({
  // 44x44 min size is needed to meet this accessibility requirement:
  // https://www.w3.org/WAI/WCAG22/Understanding/target-size-enhanced.html
  minHeight: 44,
  backgroundColor: "#FAFAFA",
});

const searchBtn_ = (theme) => ({
  width: "100%",
  minHeight: 44,
  paddingTop: "0px",
  paddingBottom: "0px",
  paddingRight: "0px",
});

const searchItem_ = (theme) => ({
  paddingTop: "2px",
  paddingBottom: "2px",
});

const listItemWrapper_ = (theme) => ({
  padding: 0,
});

const expandBtn_ = (theme) => ({
  padding: "10px",
});

const topBtn_ = (theme) => ({
  padding: "0px",
  paddingTop: "4px",
  paddingBottom: "4px",
  paddingLeft: "10px",
  display: "flex",
  justifyContent: "space-between",
  "& .MuiButton-text": {
    fontSize: "1.15rem",
    color: "rgba(0, 0, 0, 0.87)",
  },
});

const help_ = (theme) => ({
  marginTop: "auto",
});

function MenuList() {
  const dispatch = useDispatch();
  const [expandLayers, setExpandLayers] = useState(false);
  const [expandSearch, setExpandSearch] = useState(true);
  const isSmallScreen = useSelector(selectorAppIsSmallScreen);

  const mgisBtnClickHandler = () => {
    window.open("/home/index.html", "_blank");
  };

  const handleExpandLayersClick = (event) => {
    setExpandLayers(!expandLayers);
    event.stopPropagation();
  };

  const handleExpandSearchClick = (event) => {
    setExpandSearch(!expandSearch);
    event.stopPropagation();
  };

  const handleSearchClicked = (menuType, globalFilterBy) => () => {
    // Remember the equivalent searchReducer.filterBy value globally so,
    // when they next click the global SEARCH component, it will bring up
    // the same type of search.
    //
    // It is unfortunate that the these fields are close but different
    // (e.g. 'building' vs. 'buildings').  Each variant is entrenched in a
    // number of places, and it isn't worth the time/risk to consolidate
    // them.
    dispatch(actionChangeFilter(globalFilterBy));
    dispatch(actionMenuSearchTypeToggled(menuType));
    dispatch(actionMenuToggled());
    trackEvent({
      category: "Data Exploration",
      action: "Search",
    });
  };

  const openMGISDocs = () => {
    window.open(MGIS_DOC_LINK, "_blank", "noopener,noreferrer");
  };

  const handleClick = (action) => () => {
    if (action) {
      dispatch(action());
    }
    dispatch(actionMenuToggled());
  };

  const getToggleAction = (component) => {
    const actions = {
      layersVisible: actionLayerCompactToggled,
      printVisible: actionPrintExpandToggled,
      spreadsheetVisible: actionSheetExpandToggled,
      drawVisible: actionDrawExpandToggled,
    };

    return actions[component];
  };

  const handleOpenClick = (component) => () => {
    dispatch(
      actionAppToggleComponent({
        component,
        visible: true,
      }),
    );
    const actionComponentExpanded = getToggleAction(component);
    dispatch(actionComponentExpanded(true));
    dispatch(actionMenuToggled());
  };

  return (
    <>
      <List component="nav" aria-labelledby="nested-list-subheader" sx={root_}>
        <ListItem sx={topBtn_}>
          <Tooltip title="MGIS">
            <Button onClick={mgisBtnClickHandler}>MGIS</Button>
          </Tooltip>
          <MoreMenu />
        </ListItem>
        <Divider />
        <ListItem
          sx={listItemWrapper_}
          secondaryAction={
            <IconButton
              onClick={handleExpandLayersClick}
              edge="end"
              aria-label="Expand/Collapse Layers"
              sx={expandBtn_}
              size="large"
            >
              {expandLayers ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          }
        >
          <ListItemButton
            onClick={handleOpenClick("layersVisible")}
            sx={searchBtn_}
          >
            <Tooltip title="Data Overlay">
              <ListItemIcon>
                <LayersIcon />
              </ListItemIcon>
            </Tooltip>
            <ListItemText primary="Layers" />
          </ListItemButton>
        </ListItem>
        <Collapse
          in={expandLayers}
          timeout="auto"
          style={{ minHeight: "unset" }}
        >
          <Divider />
          <ListItemButton
            onClick={handleClick(actionCustomLayerListToggled)}
            sx={subsection_}
          >
            <Tooltip title="User Defined Data Overlay">
              <ListItemText primary="Additional Layer" />
            </Tooltip>
          </ListItemButton>
          <Divider />
        </Collapse>
        <ListItem
          sx={listItemWrapper_}
          secondaryAction={
            <IconButton
              onClick={handleExpandSearchClick}
              edge="end"
              aria-label="Expand/Collapse Search"
              sx={expandBtn_}
              size="large"
            >
              {expandSearch ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          }
        >
          <ListItemButton
            onClick={handleClick(actionSearchVisibleToggled)}
            sx={searchBtn_}
          >
            <Tooltip title="Search">
              <ListItemIcon>
                <SearchIcon />
              </ListItemIcon>
            </Tooltip>
            <ListItemText primary="Search" />
          </ListItemButton>
        </ListItem>
        <Collapse
          in={expandSearch}
          timeout="auto"
          style={{ minHeight: "unset" }}
        >
          <Divider />
          <ListItemButton
            onClick={handleSearchClicked("building", "buildings")}
            sx={[searchItem_, subsection_]}
          >
            <ListItemText primary="Buildings" />
          </ListItemButton>
          <ListItemButton
            onClick={handleSearchClicked("room", "rooms")}
            sx={[searchItem_, subsection_]}
          >
            <ListItemText primary="Rooms" />
          </ListItemButton>
          <ListItemButton
            onClick={handleSearchClicked("person", "people")}
            sx={[searchItem_, subsection_]}
          >
            <ListItemText primary="People" />
          </ListItemButton>
          <ListItemButton
            onClick={handleSearchClicked("department", "departments")}
            sx={[searchItem_, subsection_]}
          >
            <ListItemText primary="Departments" />
          </ListItemButton>
          <ListItemButton
            onClick={handleSearchClicked("roomtype", "roomtypes")}
            sx={[searchItem_, subsection_]}
          >
            <ListItemText primary="Room Types" />
          </ListItemButton>
          <Divider />
        </Collapse>
        {!isSmallScreen && (
          <ListItemButton onClick={handleOpenClick("printVisible")}>
            <Tooltip title="Print PDF Maps">
              <ListItemIcon>
                <PrintIcon />
              </ListItemIcon>
            </Tooltip>
            <ListItemText primary="Print" />
          </ListItemButton>
        )}
        <ListItemButton onClick={handleOpenClick("spreadsheetVisible")}>
          <Tooltip title="Custom Data Import/Export">
            <ListItemIcon>
              <GridOnIcon />
            </ListItemIcon>
          </Tooltip>
          <ListItemText primary="Spreadsheet" />
        </ListItemButton>
        {!isSmallScreen && (
          <ListItemButton onClick={handleOpenClick("drawVisible")}>
            <Tooltip title="Annotation Tools">
              <ListItemIcon>
                <EditIcon />
              </ListItemIcon>
            </Tooltip>
            <ListItemText primary="Draw/Measure" />
          </ListItemButton>
        )}
        <ListItemButton onClick={openMGISDocs} sx={help_}>
          <Tooltip title="MGIS Documentation">
            <ListItemIcon>
              <InfoIcon />
            </ListItemIcon>
          </Tooltip>
          <ListItemText primary="Help" />
        </ListItemButton>
        <UpdateStatus />
      </List>
    </>
  );
}

export default function Menu() {
  const open = useSelector((state) => state.appBarReducer.menuVisible);
  const dispatch = useDispatch();

  const toggleDrawer = (event) => {
    if (
      event &&
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }

    dispatch(actionMenuToggled());
  };

  return (
    <SwipeableDrawer open={open} onClose={toggleDrawer} onOpen={toggleDrawer}>
      {MenuList()}
    </SwipeableDrawer>
  );
}
